import {
  BarChartFill,
  CalendarCheck,
  PiggyBank,
  ShieldLock,
  Tags,
  FileEarmarkBarGraph,
} from "react-bootstrap-icons";

const FEATURES = [
  {
    icon: <PiggyBank size={28} />,
    title: "Control de ingresos y gastos",
    text: "Registra cada movimiento en segundos y mantén tu balance del mes siempre a la vista.",
  },
  {
    icon: <Tags size={28} />,
    title: "Categorías personalizadas",
    text: "Organiza tus finanzas con categorías propias: elige nombre, ícono y color para identificarlas de un vistazo.",
  },
  {
    icon: <CalendarCheck size={28} />,
    title: "Transacciones recurrentes",
    text: "Programa cargos automáticos diarios, semanales, quincenales o mensuales y olvídate de registrarlos a mano.",
  },
  {
    icon: <BarChartFill size={28} />,
    title: "Gráficas claras",
    text: "Visualiza en qué se va tu dinero con gráficas de categoría que se actualizan en tiempo real.",
  },
  {
    icon: <FileEarmarkBarGraph size={28} />,
    title: "Reportes mensuales",
    text: "Genera el resumen de cualquier mes y expórtalo a CSV para tus propios análisis o tu contador.",
  },
  {
    icon: <ShieldLock size={28} />,
    title: "Acceso seguro con Google",
    text: "Inicia sesión con tu cuenta de Google. No guardamos contraseñas ni datos bancarios.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Inicia sesión con Google",
    text: "Sin formularios largos ni contraseñas que recordar. Un clic y estás dentro.",
  },
  {
    n: "2",
    title: "Crea tus categorías",
    text: "Define cómo quieres organizar tus gastos e ingresos: renta, comida, salario, freelance, lo que necesites.",
  },
  {
    n: "3",
    title: "Registra tus movimientos",
    text: "Agrega transacciones manuales o automatiza las recurrentes para que se carguen solas.",
  },
  {
    n: "4",
    title: "Revisa tu progreso",
    text: "Consulta tu Dashboard y tus Reportes mensuales para tomar mejores decisiones con tu dinero.",
  },
];

export default function LandingContent() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 text-slate-100 sm:px-6 lg:px-8">
      <section className="py-8 text-center sm:py-12">
        <h1 className="mb-4 text-4xl font-semibold tracking-tight sm:text-5xl">
          Organiza tus finanzas personales sin complicarte
        </h1>
        <p className="mx-auto max-w-3xl text-lg text-slate-300">
          FinanzasPro es una aplicación gratuita para llevar el control de tus
          ingresos y gastos, organizarlos por categoría y entender en qué se
          va tu dinero cada mes — todo desde un solo lugar, sin hojas de
          cálculo.
        </p>
      </section>

      <section className="py-4 sm:py-6">
        <h2 className="mb-6 text-center text-2xl font-semibold">Qué puedes hacer con FinanzasPro</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {FEATURES.map((f) => (
            <article key={f.title} className="rounded-2xl border border-white/10 bg-slate-900/70 p-6 shadow-lg shadow-black/20">
              <div className="mb-3 text-sky-400">{f.icon}</div>
              <h3 className="mb-2 text-lg font-semibold">{f.title}</h3>
              <p className="text-sm leading-6 text-slate-300">{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-6 sm:py-8">
        <h2 className="mb-6 text-center text-2xl font-semibold">Cómo funciona</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-sky-500/20 text-lg font-bold text-sky-300">
                {s.n}
              </div>
              <h3 className="mb-2 font-semibold">{s.title}</h3>
              <p className="text-sm leading-6 text-slate-300">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-6 sm:py-8">
        <div className="grid items-center gap-6 lg:grid-cols-2">
          <div>
            <h2 className="mb-3 text-2xl font-semibold">Pensado para el día a día, no para contadores</h2>
            <p className="mb-3 text-slate-300">
              No necesitas saber de finanzas para usar FinanzasPro. La interfaz está pensada para que registrar un gasto tome menos de diez segundos, y para que entender tu mes tome menos de un minuto.
            </p>
            <p className="text-slate-300">
              Tus datos viven en tu cuenta, protegidos por la autenticación de Google. Nadie más tiene acceso a tu información financiera.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-6">
            <h3 className="mb-3 font-semibold">Pensado para personas que quieren:</h3>
            <ul className="list-disc space-y-2 pl-5 text-sm text-slate-300">
              <li>Saber exactamente en qué gastan cada mes</li>
              <li>Dejar de anotar gastos en notas sueltas</li>
              <li>Automatizar pagos fijos como renta o suscripciones</li>
              <li>Tener un reporte claro y exportable de sus finanzas</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="py-4 text-center text-sm text-slate-400">
        <p className="mb-1">FinanzasPro — gestor de finanzas personales.</p>
        <p className="space-x-3">
          <a href="/privacy" className="text-slate-200 underline decoration-slate-400 underline-offset-4">
            Política de privacidad
          </a>
          <a href="/terms" className="text-slate-200 underline decoration-slate-400 underline-offset-4">
            Términos de servicio
          </a>
        </p>
      </footer>
    </div>
  );
}
